import React, { useState } from "react";
import axios from "axios";
import { API, formatApiError } from "@/auth";
import { MessageCircle, Send, Mail, Phone, Loader2, Copy } from "lucide-react";

const ALL_METHODS = [["airtel", "Airtel Money"], ["orange", "Orange Money"], ["binance", "Binance Pay"], ["visa", "Visa / Carte"]];
const digits = (s) => (s || "").replace(/[^\d]/g, "");

export function ContactAdminCard({ merchant, onRequested }) {
  const [req, setReq] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const open = async () => {
    setBusy(true); setErr(null);
    try {
      const { data } = await axios.post(`${API}/payments/contact-request`);
      setReq(data);
      onRequested && onRequested();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const text = encodeURIComponent(`Bonjour, je souhaite acheter une licence LHSunlocker. Référence : ${req?.reference || ""}`);
  const contacts = req ? [
    merchant?.admin_whatsapp && { id: "whatsapp", icon: MessageCircle, label: "WhatsApp", value: merchant.admin_whatsapp, href: `https://wa.me/${digits(merchant.admin_whatsapp)}?text=${text}` },
    merchant?.admin_telegram && { id: "telegram", icon: Send, label: "Telegram", value: merchant.admin_telegram, href: `https://t.me/+${digits(merchant.admin_telegram)}` },
    merchant?.admin_email && { id: "email", icon: Mail, label: "Email", value: merchant.admin_email, href: `mailto:${merchant.admin_email}?subject=${encodeURIComponent("Licence LHSunlocker " + req.reference)}&body=${text}` },
  ].filter(Boolean) : [];

  return (
    <div className="card-lhs p-6 sm:p-8" data-testid="contact-admin-card">
      <h2 className="font-display font-extrabold text-xl sm:text-2xl">Prêt à acheter votre licence ?</h2>
      <p className="text-sm text-zinc-400 mt-2 max-w-xl">
        Contactez l'administrateur : il vous communique le prix, la durée et les instructions de paiement,
        puis active votre licence après confirmation.
      </p>
      {!req && (
        <button onClick={open} disabled={busy} data-testid="contact-admin-btn"
          className="btn-orange rounded-lg mt-6 inline-flex items-center gap-2 px-6 py-3 text-sm uppercase tracking-wider">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Phone className="w-4 h-4" />} Contacter l'administrateur pour le paiement
        </button>
      )}
      {err && <div className="mt-3 text-sm text-red-400" data-testid="contact-admin-error">{err}</div>}
      {req && (
        <div className="mt-6 space-y-4" data-testid="contact-admin-details">
          <div className="rounded-xl border border-orange-500/40 bg-orange-500/5 p-4 flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono-tech">Votre référence de demande</div>
              <div className="font-mono-tech text-xl text-orange-400 font-bold" data-testid="contact-admin-reference">{req.reference}</div>
            </div>
            <button onClick={() => navigator.clipboard.writeText(req.reference)} data-testid="contact-admin-copy-ref" className="text-zinc-400 hover:text-orange-400"><Copy className="w-4 h-4" /></button>
          </div>
          <p className="text-xs text-zinc-500">Mentionnez cette référence lorsque vous contactez l'administrateur.</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {contacts.map(c => (
              <a key={c.id} href={c.href} target="_blank" rel="noreferrer" data-testid={`contact-admin-${c.id}`}
                className="rounded-xl border border-zinc-800 bg-[#0C0C10] p-4 flex items-center gap-3 hover:border-orange-500 transition-colors">
                <c.icon className="w-5 h-5 text-orange-500 shrink-0" />
                <div className="min-w-0">
                  <div className="text-sm font-semibold">{c.label}</div>
                  <div className="text-xs text-zinc-400 font-mono-tech truncate">{c.value}</div>
                </div>
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function MethodToggles({ state, onChange }) {
  const enabled = state.enabled_methods || [];
  const toggle = (id) => onChange({
    ...state, enabled_methods: enabled.includes(id) ? enabled.filter(x => x !== id) : [...enabled, id],
  });
  return (
    <div className="mt-4" data-testid="cfg-methods">
      <div className="text-[11px] uppercase text-zinc-500 mb-2 font-mono-tech">Moyens de paiement affichés aux techniciens</div>
      <div className="flex flex-wrap gap-2">
        {ALL_METHODS.map(([id, label]) => (
          <button type="button" key={id} onClick={() => toggle(id)} data-testid={`cfg-method-${id}`}
            className={`px-3 py-1.5 rounded-lg border text-xs transition-colors ${enabled.includes(id) ? "border-orange-500 bg-orange-500/10 text-orange-400" : "border-zinc-800 text-zinc-500"}`}>
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}
